import { useEffect, useReducer } from 'react';
import { ENV } from '@/app/config';
import { logInfo, logError } from '@/services/observability';
import type { TABTestAction, TABTestConfig, TABTestConfigEntry, TABTestState } from './ab-test.types';
import { exposeABTestDebug } from './ab-test.debug';

const AB_CACHE_KEY = 'wm_ab_tests';

const loadCache = (): Record<string, string | null> => {
	try {
		return JSON.parse(localStorage.getItem(AB_CACHE_KEY) ?? '{}') as Record<string, string | null>;
	} catch {
		return {};
	}
};

const saveCache = (data: Record<string, string | null>): void => {
	localStorage.setItem(AB_CACHE_KEY, JSON.stringify(data));
};

/**
 * Reads cache and removes entries for tests that are no longer active
 * in VITE_AB_TEST_CONFIG, keeping localStorage clean.
 */
const loadValidatedCache = (activeKeys: ReadonlyArray<string>): Record<string, string | null> => {
	const raw = loadCache();
	const valid = Object.fromEntries(
		Object.entries(raw).filter(([key]) => activeKeys.includes(key))
	);

	if (Object.keys(raw).length !== Object.keys(valid).length) {
		saveCache(valid);
	}

	return valid;
};

const reducer = (
	prev: Record<string, TABTestState>,
	action: TABTestAction
): Record<string, TABTestState> => ({
	...prev,
	[action.testName]: {
		variation: action.variation,
		isLoading: action.isLoading,
		fetched: action.fetched
	}
});

const initState = (configs: TABTestConfig): Record<string, TABTestState> => {
	const cache = loadValidatedCache(Object.keys(configs));

	return Object.fromEntries(
		Object.keys(configs).map(name => {
			const cached = cache[name] ?? null;
			const hasCached = cached !== null;

			return [name, { variation: cached, isLoading: !hasCached, fetched: hasCached }];
		})
	);
};

/**
 * Fetches all A/B test variations from Adobe Target based on VITE_AB_TEST_CONFIG.
 *
 * For each test entry in the config:
 * 1. Checks localStorage cache first (instant resolve)
 * 2. Falls back to window.adobe.target.getOffer() call
 * 3. Caches successful results in localStorage
 *
 * When AB_TEST_ENABLED is false or config is empty, returns an empty record.
 *
 * This hook is consumed by ABTestProvider — components should use
 * useABTest() or useABTestFor(key) from the context instead.
 */
const useAllABTests = (): Record<string, TABTestState> => {
	const configs: TABTestConfig = ENV.AB_TEST_ENABLED ? ENV.AB_TEST_CONFIG : {};

	const [state, dispatch] = useReducer(reducer, undefined, () => initState(configs));

	useEffect(() => {
		const entries = Object.entries(configs) as ReadonlyArray<[string, TABTestConfigEntry]>;

		if (!window.adobe?.target?.getOffer) {
			logInfo('[ab-test] Adobe Target not available — falling back to default variations');

			entries.forEach(([testName]) => {
				dispatch({ testName, variation: null, isLoading: false, fetched: true });
			});
			return;
		}

		entries.forEach(([testName, cfg]) => {
			window.adobe!.target!.getOffer({
				mbox: cfg.mboxName,
				success: (offer) => {
					const variation = (offer?.[0]?.content?.[0]?.[cfg.key] ?? null) as string | null;

					saveCache({ ...loadCache(), [testName]: variation });
					dispatch({ testName, variation, isLoading: false, fetched: true });

					logInfo('[ab-test] Resolved variation', {
						testName,
						mbox: cfg.mboxName,
						variation: variation ?? 'null'
					});
				},
				error: (error) => {
					dispatch({ testName, variation: null, isLoading: false, fetched: true });

					logError('[ab-test] Failed to resolve variation', {
						testName,
						mbox: cfg.mboxName,
						error: String(error)
					});
				}
			});
		});
	}, []);

	const configKeys = Object.keys(configs);
	const allFetched = configKeys.length > 0 && Object.values(state).every(t => t.fetched);

	useEffect(() => {
		if (allFetched) {
			exposeABTestDebug(state, configs);
		}
	}, [allFetched]);

	return state;
};

export { useAllABTests };
